import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { formatMomentDate } from "../../../../../utils/dateHelper";
import { fetchMarketplaceListings, deleteUserListing, toggleListingActiveStatus } from "../../../../../redux/slice/marketplaceSlice";
import CustomTable from "../../../../../components/table/CustomTable";
import { PlusIcon } from "../../../../../assets/icons";
import { routes } from "../../../../../routes/pageRoutes";
import DeletePopup from "../../../../../components/delete-popup";
import PreviewModal from "../../../../../components/modals/previewModal";
import { usePermission } from "../../../../../hooks/usePermission";
import { useToast } from "../../../../../context/TostContext";
import StatusChip from "../../../../../components/statusChip/StatusChip";

const MODULE_SLUG = "marketplace_bulk_offers";

const getStatusBg = (status) => {
  const lowerStatus = status?.toLowerCase();
  switch (lowerStatus) {
    case "pending":
      return "warning";
    case "approved":
      return "success";
    case "published":
      return "blue";
    case "rejected":
      return "danger";
    case "closed":
      return "secondary";
    default:
      return "";
  }
};

const GroupBuyOffers = () => {
  const { id: companyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewMediaArray, setPreviewMediaArray] = useState([]);
  const [previewInitialIndex, setPreviewInitialIndex] = useState(0);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const { listings, pagination, isLoading } = useSelector((state) => state.marketplace);
  const { companyDetail } = useSelector((state) => state.company);

  const loadListings = useCallback(() => {
    if (companyId) {
      dispatch(
        fetchMarketplaceListings({
          page,
          limit,
          seller_id: companyId,
          seller_type: "company",
        })
      );
    }
  }, [companyId, page, limit, dispatch]);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  const handleDeleteOffer = () => {
    if (!itemToDelete) return;
    setDeleting(true);
    dispatch(deleteUserListing(itemToDelete))
      .unwrap()
      .then(() => {
        toast.success("Offer deleted successfully");
        setShowDeletePopup(false);
        setItemToDelete(null);
        loadListings();
      })
      .catch((error) => {
        toast.error(error || "Failed to delete offer");
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  const handleToggleActive = (offerId, checked) => {
    setTogglingId(offerId);
    dispatch(toggleListingActiveStatus({ id: offerId, is_active: checked }))
      .unwrap()
      .then(() => {
        toast.success(`Offer ${checked ? "activated" : "deactivated"} successfully`);
        loadListings();
      })
      .catch((error) => {
        toast.error(error || "Failed to toggle offer status");
      })
      .finally(() => {
        setTogglingId(null);
      });
  };

  const actionList = [
    {
      title: "View",
      handler: (row) => {
        navigate(routes.viewMarketplaceBulkOffer.replace(":id", row.id));
      },
    },
    {
      title: "Edit",
      handler: (row) => {
        navigate(routes.editMarketplaceBulkOffer.replace(":id", row.id), {
          state: { from: window.location.pathname + window.location.search }
        });
      },
    },
    {
      title: "Delete",
      handler: (row) => {
        setItemToDelete(row.id);
        setShowDeletePopup(true);
      },
      isDanger: true,
    },
  ];

  const columns = [
    {
      header: "Images",
      cell: (_, item) => {
        const photos = item.a_plus_photos?.length > 0 ? item.a_plus_photos : (item.photos || []);
        const firstPhoto = photos[0]?.url;

        return firstPhoto ? (
          <div
            className="position-relative rounded overflow-hidden border shadow-sm"
            style={{ width: "50px", height: "50px", flexShrink: 0, backgroundColor: "#f8f9fa", cursor: "pointer" }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const mediaArray = photos.map((photo) => ({
                url: photo.url || photo.file?.url,
                title: item.name || "Offer Image",
                type: "image",
              }));
              setPreviewMediaArray(mediaArray);
              setPreviewInitialIndex(0);
              setShowPreviewModal(true);
            }}
          >
            <img src={firstPhoto} alt={item.name} className="w-100 h-100 object-fit-cover" />
          </div>
        ) : (
          <div
            className="bg-light rounded border d-flex align-items-center justify-content-center text-muted small"
            style={{ width: "50px", height: "50px", flexShrink: 0 }}
          >
            N/A
          </div>
        );
      },
    },
    {
      header: "Offer Name",
      cell: (_, item) => (
        <div className="fw-medium text-truncate" style={{ maxWidth: "200px" }}>
          {item.name || "-"}
        </div>
      ),
    },
    {
      header: "Category",
      cell: (_, item) => item.category?.name || "-",
    },
    {
      header: "SKU",
      cell: (_, item) => item.company_product_details?.sku || "-",
    },
    {
      header: "Variants",
      cell: (_, item) => item.company_product_details?.variations || "-",
    },
    {
      header: "Country of Origin",
      cell: (_, item) => item.company_product_details?.country?.name || "-",
    },
    {
      header: "State",
      cell: (_, item) => item.state?.name || "-",
    },
    {
      header: "City",
      cell: (_, item) => item.city?.name || "-",
    },
    {
      header: "Serviceability",
      cell: (_, item) => {
        const type = item.company_offer_details?.serviceability_type;
        return type ? type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "-";
      },
    },
    {
      header: "MRP",
      cell: (_, item) => (item.company_offer_details?.mrp ? `₹${item.company_offer_details.mrp}` : "-"),
    },
    {
      header: "Offer Price",
      cell: (_, item) => (item.company_offer_details?.offer_price ? `₹${item.company_offer_details.offer_price}` : "-"),
    },
    {
      header: "Discount",
      cell: (_, item) => (item.company_offer_details?.discount ? `${item.company_offer_details.discount}%` : "-"),
    },
    {
      header: "Lot Size",
      cell: (_, item) => item.company_offer_details?.lot_size || "-",
    },
    {
      header: "Available Qty",
      cell: (_, item) => item.company_offer_details?.available_qty || "-",
    },
    {
      header: "Remaining Qty",
      cell: (_, item) => item.company_offer_details?.remaining_qty || "-",
    },
    {
      header: "Booking Deposit",
      cell: (_, item) => (item.company_offer_details?.booking_deposit ? `₹${item.company_offer_details.booking_deposit}` : "-"),
    },
    {
      header: "Status",
      cell: (_, item) =>
        item.status ? (
          <StatusChip bg={getStatusBg(item.status)} pill title={item.status} className="text-capitalize" />
        ) : (
          "-"
        ),
    },
    {
      header: "Active",
      cell: (_, item) => (
        <div className="form-check form-switch custom-switch" onClick={(e) => e.stopPropagation()}>
          <input
            className="form-check-input cursor-pointer"
            type="checkbox"
            role="switch"
            checked={item?.is_active}
            disabled={togglingId === item.id || !hasPermission(MODULE_SLUG, "update")}
            onChange={(e) => handleToggleActive(item.id, e.target.checked)}
          />
        </div>
      ),
    },
    {
      header: "Created At",
      cell: (_, item) => (item.created_at ? formatMomentDate(item.created_at, "DD-MM-YYYY hh:mm A") : "-"),
    },
    {
      header: "Updated At",
      cell: (_, item) => (item.updated_at ? formatMomentDate(item.updated_at, "DD-MM-YYYY hh:mm A") : "-"),
    },
  ];

  const totalPages = pagination?.total_pages || pagination?.pages || 0;
  const totalItems = pagination?.total || 0;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h5 className="fw-bold mb-0" style={{ fontSize: "16px", color: "#444050" }}>
          Group Buy Offers
        </h5>
        {hasPermission(MODULE_SLUG, "create") && (
          <button
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
            onClick={() => {
              navigate(routes.createMarketplaceBulkOffer, {
                state: {
                  from: window.location.pathname + window.location.search,
                  companyId: companyId,
                  companyName: companyDetail?.name || ""
                }
              });
            }}
          >
            <PlusIcon size={14} />
            Add Group Buy Offer
          </button>
        )}
      </div>

      <div className="border rounded-3 overflow-hidden">
        <CustomTable
          columns={columns}
          data={listings || []}
          loading={isLoading}
          actionList={actionList}
          emptyMessage="No group buy offers found."
          pagination={{
            currentPage: page,
            totalPages,
            total: totalItems,
            onPageChange: (newPage) => setPage(newPage),
            pageSize: limit,
            setPageSize: (newLimit) => {
              setLimit(newLimit);
              setPage(1);
            },
            setCurrentPage: (newPage) => setPage(newPage),
          }}
        />
      </div>

      <DeletePopup
        show={showDeletePopup}
        onHide={() => {
          setShowDeletePopup(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteOffer}
        isLoading={deleting}
        saveTitle="Delete"
      />

      <PreviewModal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        previewMedia={previewMediaArray}
        initialIndex={previewInitialIndex}
      />
    </div>
  );
};

export default GroupBuyOffers;
